import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ResponsiveService, WindowService } from '@frk/eds-components';
import { InsightsService } from '@marketing/insights/insights.service';
import { getMonthDiffrence } from '@marketing/marketing-utils';
import { WINDOW } from '@ng-web-apis/common';

import {
  AppStateService,
  GlobalConfigService,
  LocalLinkService,
  SchemaService,
  SegmentService,
  SiteConfigService,
  WidenService,
  WindowScrollService,
} from '@services';
import { AbstractBaseComponent } from '@shared/abstract-base/abstract-base.component';
import { CardContent, Link, SiteParams, WidenDocumentJson } from '@types';
import { Logger } from '@utils/logger';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LinkService } from '@services/link.service';
import {
  AlternativeArticle,
  ArticleSpacing,
  ArticleContent,
  PageContents,
  TargetedLinkCompound,
  CardData,
  ArticleProfileSetting,
  BiographyDynamicProfile,
  BiographyDynamicProfileJson,
  ContributorsBiography,
} from './article.types';
import get from 'lodash/get';
import { ArticleService } from './services/article.service';
import { ViewModeService } from '@services/view-mode.service';
import {
  RelateArticleItemsDTO,
  RelatedArticleResults,
} from '@search/interfaces/insights.interface';
import { PageViewService } from '@services/page-view.service';

export interface TargetedButtons {
  title: string;
  subTitle: string;
  iconType?: string;
  linkBtnLabel?: string;
  linkBtnHref?: string;
  theme?: string;
  isExternalLink?: boolean;
  signInRequired?: boolean;
  target?: string;
  displayText?: string;
}

/**
 * Logger
 */
const logger = Logger.getLogger('ArticleComponent');

/**
 * The Article component can be used to display article page
 *
 * Selector: `ft-article-blocks`
 *
 * Exported as: `ArticleComponent`
 */
@Component({
  selector: 'ft-article-blocks',
  templateUrl: './article.component.html',
  styleUrls: ['./article.component.scss'],
})
export class ArticleComponent
  extends AbstractBaseComponent
  implements OnDestroy, OnInit, AfterViewInit {
  // Public Fields
  showButtons = false;
  sidebarOffsetTop: number;
  sidebarHeight: number;
  translationLabel: any;
  relatedContentHeaderTypeLabel: any;
  showArticleInfo: boolean;
  content: ArticleContent;
  isContactAvailable: boolean;
  segmentTags: string;
  pageTheme: string;
  buttontype: string;
  desktopImage: string;
  mobileImage: string;
  insightsResource: any;
  jumplinkDet: PageContents = {
    jumplinks: [],
  };
  compactdet = false;
  siteParams: SiteParams;
  simLogoResourceBundle: any;
  simLogoLinkResourceBundle: object;
  public isSingleCard = false;
  public segmentOk = false;
  public widenMetaData: WidenDocumentJson;
  public pageType: string;
  public targetedButtons?: TargetedButtons[] = [];
  public isLoggedIn: boolean;
  public showRelatedArticle: boolean;
  // Element Refs
  @ViewChild('sidebar') sidebar: ElementRef;

  // Observables
  isMobile$: Subscription;
  isMobile: boolean;
  isHandheld$: Subscription;
  isHandheld: boolean;
  isfragement$: Subscription;

  // Private fields
  private unsubscribe$: Subject<void> = new Subject<void>();
  public showArticle = false;
  articleBlock: ArticleContent;
  public isDynamicRelatedArticle = false;
  public relateArticleData: RelateArticleItemsDTO[] = [];
  public articleProfileSetting: ArticleProfileSetting;
  public noArticleContent: string;
  public baseProfileDocumentPath =
    '/content/documents/biographies/corporate-biographies/en-us/';

  /**
   * Constructor
   */
  constructor(
    private responsiveService: ResponsiveService,
    private renderer2: Renderer2,
    @Inject(DOCUMENT) private doc: Document,
    @Inject(WINDOW) private windowRef: Window,
    private widenService: WidenService,
    private appStateService: AppStateService,
    private segmentService: SegmentService,
    private insightsService: InsightsService,
    private router: ActivatedRoute,
    private route: Router,
    private siteConfig: SiteConfigService,
    private localLinkService: LocalLinkService,
    private pageConfigService: GlobalConfigService,
    private schemaService: SchemaService,
    private scrollService: WindowScrollService,
    private ctaLinkService: LinkService,
    private articleService: ArticleService,
    private windowService: WindowService,
    private cdr: ChangeDetectorRef,
    private viewModeService: ViewModeService,
    private pageViewService: PageViewService
  ) {
    super();
    this.siteParams = this.pageConfigService.getSiteParams();
    this.segmentTags = this.segmentService.getCurrentSegment()?.insightAudiences;
    this.isEditMode = this.viewModeService.isEditMode();
  }
  ngOnInit(): void {
    super.ngOnInit();

    this.pageType = this.page.getComponent()?.getModels()?.pageData?.pageType;

    // Fetching data from the component directly due to no BR method available.
    this.articleProfileSetting = get(
      this.page?.getComponent('main-content')?.getChildren()[0],
      ['model']
    )?.meta?.params;

    // Set breadcrumbs
    this.setBreadcrumbItems();

    // Responsive behaviours
    this.isMobile$ = this.responsiveService
      .isMobile$()
      ?.subscribe((isMobile) => (this.isMobile = isMobile));
    this.isHandheld$ = this.responsiveService
      .isHandheld$()
      ?.subscribe((isHandheld) => (this.isHandheld = isHandheld));
    if (this.isMobile) {
      this.showButtons = true;
      this.compactdet = true;
    }

    // Translations and labels
    this.translationLabel = this.component?.getModels()['ft.core.article'];

    this.relatedContentHeaderTypeLabel = this.component?.getModels()[
      'ft.core.article-related-content'
    ];
    this.simLogoResourceBundle = this.component?.getModels()[
      'ft.core.sim-logos'
    ];
    this.simLogoLinkResourceBundle = this.component?.getModels()[
      'ft.core.sim-logos-links'
    ];

    // Load content from resourceapi
    this.content = this.getContent();

    // show stickty side bar if localizable data has isAllowed property as true;
    if (
      this.content &&
      this.content?.relatedContent?.length &&
      this.content?.relatedContent[0]?.localizableLink?.length
    ) {
      this.content.relatedContent[0].localizableLink.forEach((data) => {
        if (data.isAllowed) {
          this.showArticle = true;
        }
      });
    }

    // Add Schema
    this.createArticleSchema();

    if (this.content?.targetingLinkCompound?.length) {
      this.setTargetedButtonsData();
    }

    // Set Meta Tags
    const metadata = get(this.page, ['model', 'page', 'models', 'pageData']);
    const imageUrl = this.articleService.getSocialMediaImg(this.content);
    this.articleService.setMetaTags(metadata, imageUrl, this.content?.title);

    this.widenMetaData = this.ctaLinkService.getWidenDocumentParams(
      this.content?.linkWidenAsset?.widenAssetCompound?.widenDocument
    );

    // NGC-13630 : Removed HostListener and added windowService
    this.windowService
      .getScrollObs$(100)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        if (this.isMobile) {
          return;
        }
        const windowScroll = this.windowRef.scrollY;
        this.sidebarOffsetTop = this.sidebar?.nativeElement.offsetTop;
        this.sidebarHeight = this.sidebar?.nativeElement.offsetHeight;
        this.showButtons =
          windowScroll > this.sidebarOffsetTop + this.sidebarHeight;
        this.cdr.detectChanges();
      });

    // Trigger Pageview
    this.pageViewService.triggerArticlePageView(this.content);
  }

  /**
   * Angular hook
   */
  ngAfterViewInit(): void {
    try {
      this.scrollService.scrollToAnchor(this.router.snapshot.fragment);
    } catch (e) {}
  }

  /**
   * Angular hook
   */
  ngOnDestroy() {
    this.isMobile$?.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Get content from document
   */

  getContent(): ArticleContent {
    const article = this.document?.getData<ArticleContent>();

    // TODO: Remove this line once rightRails has been identified
    logger.debug(article);

    if (article) {
      // Content type specific logic
      if (article?.contentType) {
        switch (article.contentType) {
          case 'ftcore:Article':
            this.pageTheme = 'primary';
            this.buttontype = 'secondary-dark';
            break;
          case 'ftcore:PressRelease':
            this.pageTheme = 'highlight-blue';
            this.buttontype = 'secondary';
            break;
          default:
            this.pageTheme = 'primary';
            this.buttontype = 'tertiary';
        }
      }
      // Set archive flag
      if (!article?.doNotArchive) {
        if (getMonthDiffrence(article?.isodate) > 12) {
          article.isArchive = true;
        }
      }
      // Set archive label based on flag
      if (article?.isArchive && !article?.hideDate) {
        article.archiveLabel = ' - ' + this.translationLabel?.archive;
      } else if (article?.isArchive && article?.hideDate) {
        article.archiveLabel = this.translationLabel?.archive;
      } else {
        article.archiveLabel = '';
      }
      // Set isodate
      if (article?.isodate) {
        article.isodate = this.insightsService.getLongDate(article.isodate);
      }
      // Set Article Banner Image
      if (article?.image?.widenDocument) {
        article.imgSrc = this.widenService.getWidenImageVariantUrl(
          this.widenService.getWidenAssetUrl(article.image?.widenDocument),
          'original',
          'webp',
          'eds-banner-article-insights'
        );
      } else {
        // fallback for empty image source
        article.imgSrc = 'javascript:void(0)';
      }

      // Set local pdf
      if (article?.localizablePdf?.length) {
        this.articleService.setLocalizablePdf(this.segmentTags, article);
      }
      // Widen Document
      if (article?.audio?.widenDocument) {
        article.audioHref = this.widenService.getWidenAssetUrl(
          article.audio?.widenDocument
        );
      }
      // Widen Asset link
      if (article?.linkWidenAsset) {
        article.pdf = [
          {
            displayText: article.linkWidenAsset?.displayText,
            href: this.widenService.getWidenAssetUrl(
              article.linkWidenAsset?.widenAssetCompound?.widenDocument
            ),
            isNewWindow: article.linkWidenAsset?.target === '_blank',
          },
        ];
        logger.debug(article.pdf);
      }

      // For New Press Release document.
      if (article?.contributors) {
        this.articleService.formatContributors(
          article,
          this.page,
          this.articleProfileSetting
        );
      }

      // Contributors & Authors
      if (article?.insightMetadata?.contributors) {
        this.articleService.formatContributors(article, this.page);
        this.articleService.formatAuthors(article, this.page);
      }

      // Format metadata
      this.articleService.formatMetadata(article);

      // Alternative content format
      if (article?.insightMetadata?.alternativeContent) {
        let alternativeArticleData: AlternativeArticle;
        alternativeArticleData = this.page
          .getContent(article.insightMetadata.alternativeContent)
          .getData<AlternativeArticle>();
        alternativeArticleData.link = this.page
          .getContent(article?.insightMetadata?.alternativeContent)
          .getUrl();
        article.alternativeArticle = alternativeArticleData;
      }

      // Sims logo
      if (article?.insightMetadata?.logo) {
        article.SIM = this.insightsService.getSIMLogo(
          article?.insightMetadata?.logo,
          false,
          this.simLogoResourceBundle
        );
        if (this.simLogoLinkResourceBundle) {
          article.SIM.logoHref = this.simLogoLinkResourceBundle[
            `logo-${article?.insightMetadata?.logo}`
          ];
        }
      }
      if (article?.audienceExclusions) {
        this.segmentOk = this.articleService.isSegmentTagAvailable(
          this.segmentTags,
          JSON.stringify(article?.audienceExclusions)
        );
      } else {
        this.segmentOk = false;
      }
      // General content formatting
      if (article?.content) {
        article.contentBlocks = [];
        for (const contentBlock of article.content) {
          contentBlock.spacingBottom = this.setSpacingBottom(contentBlock);
          // Localizable card
          if (contentBlock?.cardCompound) {
            if (contentBlock?.cardCompound.length > 0) {
              contentBlock?.cardCompound.forEach((card, i) => {
                this.getSingleCardValue(card);
                contentBlock.cardCompound[i] = this.formatCard(card);
              });
            } else {
              this.getSingleCardValue(contentBlock.cardCompound);
              contentBlock.cardCompound = this.formatCard(
                contentBlock.cardCompound
              );
            }

            // Check should we show it
            if (contentBlock.targeting) {
              contentBlock.isAllowed = this.articleService.isSegmentTagAvailable(
                this.segmentTags,
                contentBlock.targeting
              );
            } else {
              contentBlock.isAllowed = true;
            }
          }

          this.mobileImage = contentBlock?.mobileImage?.widenDocument;
          this.desktopImage = contentBlock?.widenAsset;
          if (this.isMobile && contentBlock?.mobileImage?.widenDocument) {
            contentBlock.widenAsset = this.mobileImage;
          } else {
            contentBlock.widenAsset = this.desktopImage;
          }

          if (contentBlock.type === 'ParagraphBlock') {
            contentBlock.content = this.localLinkService.getLocalizeLinks(
              contentBlock.content
            );
            contentBlock.spacingBottom = contentBlock.spacingBottom || 'none';
          }

          article?.contentBlocks.push(contentBlock);
        }
        // Set local content
        if (article?.contentBlocks?.length) {
          article.contentBlocks.forEach((data) => {
            if (data.type === 'ParagraphBlock') {
              this.articleService.setLocalizableContent(
                this.segmentTags,
                article
              );
              article.contentBlocks = article?.contentBlocks?.filter((item) =>
                article?.filterContent?.includes(item)
              );
            }
          });
        }
      }

      // Format jumplinks
      if (article?.jumpLinks?.length) {
        const updateUrl = this.route.createUrlTree([], {
          relativeTo: this.router,
        });
        for (const element of article.jumpLinks) {
          this.jumplinkDet.jumplinks.push({
            id: updateUrl + '#' + element.anchor,
            title: element.heading,
          });
        }
      }

      // Article Related Content
      if (article?.relatedContent?.length) {
        this.isDynamicRelatedArticle =
          article?.relatedContent[0]?.dynamicFilters?.displayDynamicRelatedContent;
        if (this.isDynamicRelatedArticle) {
          const maxLimit =
            article?.relatedContent[0]?.dynamicFilters?.maxResults;
          this.articleService
            .getRelatedArticleContent$(this.windowRef.location.href, maxLimit)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((data: RelatedArticleResults) => {
              this.relateArticleData = data?.results[0]?.response?.hits;

              this.relateArticleData.forEach((item) => {
                item._source.description = this.truncateString(
                  item?._source?.cardSummary
                );
              });
            });
        }
        this.articleService.getRelatedContent(
          this.segmentTags,
          article,
          this.page
        );
        // to check if data present will be visible for end user or not
        article?.relatedContent?.forEach((data) => {
          data?.localizableLink?.forEach((localizeLink) => {
            if (localizeLink.isAllowed) {
              this.showRelatedArticle = true;
            }
          });
        });
      }

      // Targeted Buttons
      if (article?.targetingLinkCompound?.length) {
        this.articleService.setTargetingLinkCompound(
          this.segmentTags,
          article,
          this.page
        );
      }

      // Display cards format
      if (article?.displayInsightsCards) {
        // Check for cms title override
        const title = this.relatedContentHeaderTypeLabel[
          article?.insightsCardsHeader
        ];

        article.insightsCardsFilters.displayStyle = 'ArticleCards';
        article.insightsCardsFilters.preTitle = this.translationLabel?.insights;
        article.insightsCardsFilters.title = title
          ? title
          : this.translationLabel?.relatedArticle;
        article.insightsCardsFilters.enableViewMore = article
          .insightsCardsFilters.hideViewMoreLink
          ? false
          : true;
        this.insightsResource = this.component?.getModels()[
          'ft.core.insightsCards'
        ];
      }
      if (
        article?.company ||
        article?.contactName ||
        article?.contactTelephone
      ) {
        this.isContactAvailable = true;
      }

      // Hide contact block when hideContactBlock is true
      if (this.articleProfileSetting?.hideContactBlock) {
        delete article.company;
        delete article.contactName;
        delete article.contactTelephone;
        this.isContactAvailable = false;
      }

      // Set showArticleInfo flag
      if (
        article?.insightMetadata?.logo ||
        article?.alternativeArticle ||
        article?.contributors?.length ||
        article?.sections?.length ||
        this.isContactAvailable ||
        article?.contributorsBiography?.length
      ) {
        this.showArticleInfo = true;
      }
      // still righRail type is not defined as each element of an array is of different type
      if (article?.rightRail) {
        article?.rightRail?.forEach((data) => {
          data.spacingBottom = this.setSpacingBottom(data);
          if (data.style === 'Promo Card' || data.style === 'Media Card') {
            data.cardCompound.forEach((card) => {
              this.setRightRailPromoCardDetails(card, data);
            });
          }
          if (data.type === 'ParagraphBlock') {
            // WDE-6412 : Paragraph block targeting
            if (data?.targeting) {
              data.isAllowed = this.articleService.isSegmentTagAvailable(
                this.segmentTags,
                data.targeting
              );
            } else {
              data.isAllowed = true;
            }
            data.content = this.localLinkService.getLocalizeLinks(data.content);
          }
        });
      }
      if (
        article?.linkURL &&
        article?.linkURL?.displayText &&
        article?.linkURL?.url
      ) {
        this.targetedButtons.push({
          title: article?.linkURL?.title,
          subTitle: article?.linkURL?.displayText,
          iconType: article?.linkURL?.data?.iconType,
          linkBtnLabel: article?.linkURL?.displayText,
          linkBtnHref: article?.linkURL?.url,
          theme: article?.linkURL?.iconType,
          isExternalLink: article?.linkURL?.external,
          signInRequired: article?.linkURL?.signInRequired,
          target: article?.linkURL?.target,
          displayText: article?.linkURL?.displayText,
        });
      }

      // Handle Biographies
      if (article?.contributorsBiography?.length) {
        this.getBiosProfilesData(article);
      }
    }
    return article;
  }

  /**
   * This method sets the card object for right rail EDS promo card
   * @param card of type CardContent
   * @param data of type CardData
   */
  public setRightRailPromoCardDetails(card: CardContent, data: CardData): void {
    const linkCollection = data?.cardCompound[0]?.link
      ? data?.cardCompound[0]?.link?.linkCollection[0]
      : data.link.linkCollection[0];
    const documentRef = linkCollection?.document?.$ref;
    const internalLink = this.articleService.getCardLink(
      this.page,
      documentRef
    );

    card.imageSrc = this.widenService.getWidenAssetUrl(card?.image?.widenAsset);
    card.href =
      internalLink ||
      this.ctaLinkService.getCTALink(
        this.page,
        linkCollection,
        this.appStateService?.getHomePageUrl()
      );
    card.externalLink = linkCollection?.external;
    card.externalSuffix = linkCollection?.externalSuffix;
    card.isNewWindow = linkCollection?.target;

    if (data?.targeting) {
      data.isAllowed = this.articleService.isSegmentTagAvailable(
        this.segmentTags,
        data.targeting
      );
    } else {
      data.isAllowed = true;
    }
  }

  /**
   * This method transforms the content returned from bloomreach
   * into the format expected in the EDS component.
   *
   * DUPLICATE CODE!! - MOVE TO UTILITY
   *
   * @param card object returned from the document parameter
   */
  formatCard(card: CardContent) {
    card.body = card.content;

    // Use link collection if it exists (seems to be a difference between compounds)
    if (card.linkCompound?.linkCollection?.length) {
      card.linkCompound = card.linkCompound.linkCollection[0];
    } else if (card.link?.linkCollection?.length) {
      card.linkCompound = card.link.linkCollection[0];
    }

    this.setPromoCardDetails(card, card.linkCompound);

    return card;
  }

  /**
   * This method sets the card object for EDS promo card
   * @param card of type CardContent
   * @param linkCompound of type Link
   */
  public setPromoCardDetails(card: CardContent, linkCompound: Link): void {
    const documentRef = linkCompound?.document?.$ref;
    const internalLink = this.articleService.getCardLink(
      this.page,
      documentRef
    );

    card.imageSrc = this.widenService.getWidenAssetUrl(card?.image?.widenAsset);
    card.href =
      internalLink ||
      this.ctaLinkService.getCTALink(
        this.page,
        linkCompound,
        this.appStateService?.getHomePageUrl()
      );
    card.externalLink = linkCompound?.external;
    card.externalSuffix = linkCompound?.externalSuffix;
    card.isNewWindow = linkCompound?.target;
    card.ctaText = linkCompound?.displayText;
    card.signInRequired = linkCompound?.signInLink;
    card.theme = this.theme || 'primary';

    if (card.image) {
      card.imageSrc = this.widenService.getWidenAssetUrl(card.image.widenAsset);
      card.imageSrc = this.widenService.getWidenImageVariantUrl(
        card.imageSrc,
        'original',
        'webp'
      );
    }
  }

  /**
   * Create article schema
   */
  createArticleSchema() {
    this.schemaService.insertSchema(
      SchemaService.insightArticleSchema(
        this.content?.title,
        this.content?.isodate,
        this.content?.imgSrc,
        this.content?.contributors?.map((author) => author.fullName)
      ),
      'structured-data'
    );
  }

  private setTargetedButtonsData(): void {
    this.content?.targetingLinkCompound?.forEach(
      (data: TargetedLinkCompound) => {
        if (data.isAllowed) {
          this.targetedButtons.push({
            title: data?.link?.title,
            subTitle: data?.link?.title,
            iconType: data?.iconType,
            linkBtnLabel: data?.link?.displayText,
            linkBtnHref: data?.link?.url,
            theme: data?.iconType,
            isExternalLink: data?.link?.external,
            signInRequired: data?.link?.signInRequired,
            target: data?.link?.target,
            displayText: data?.link?.displayText,
          });
        }
      }
    );
  }

  /**
   * Calculates value of singleCard that is passed to eds-component from template file
   * @param cardCompound object of cardCompound
   */
  public getSingleCardValue(cardCompound) {
    this.isSingleCard = cardCompound?.image?.widenAsset ? false : true;
  }

  /**
   * Sets eds margin bottom for various articleBlocks
   * @param articleBlock object have attributes required to set spacing
   * @returns margin bottom string
   */

  private setSpacingBottom(articleBlock: ArticleSpacing): string {
    // Checking if type !== TitleBlock inorder to skip Title Block from 'xl' spacing
    // Since Eds title block has default 'md' spacing
    if (articleBlock?.type !== 'TitleBlock') {
      return articleBlock?.spacingBottom || 'xl';
    } else {
      return 'none';
    }
  }

  private truncateString(str: string): string {
    if (str?.length > 200) {
      return str.slice(0, 200) + '...';
    }
    return str;
  }

  /**
   * Fetch Biography profiles
   */
  private getBiosProfilesData(article: ArticleContent): void {
    const biographyPageUrl = this.siteConfig.getGlobalConfig()
      ?.siteConfiguration?.site?.biographyPageUrl;
    const bioProfiles: ContributorsBiography[] = article.contributorsBiography;
    const biosProfileItems: BiographyDynamicProfile[] = [];
    if (bioProfiles.length) {
      for (const profile of bioProfiles) {
        if (profile?.biographyDynamicProfile) {
          const biographyInfo: BiographyDynamicProfileJson = JSON.parse(
            profile?.biographyDynamicProfile
          );
          const employeeId: string = biographyInfo?.employeeId;
          const biosProfileData: BiographyDynamicProfile = biographyInfo;
          biosProfileData.fullName = biographyInfo?.name;
          biosProfileData.overview = this.setOverviewText(biographyInfo);
          biosProfileData.profileImageUrl = this.checkProfileImageUrl(
            biographyInfo
          );
          if (biographyPageUrl !== undefined && biographyPageUrl !== '') {
            biosProfileData.profilePath = this.createProfilePageUrl(
              biographyInfo,
              biographyPageUrl
            );
          }
          biosProfileData.fallbackImgSrc = '/assets/images/ben_head.png';
          biosProfileItems.push(biosProfileData);
          this.cdr.detectChanges();
        }
      }
      article.biographyDynamicProfile = biosProfileItems;
    }
  }

  /**
   * This method sets the overview text by combining job title's
   * @param biosProfileData from BR
   * @returns overview text
   */
  private setOverviewText(
    biosProfileData: BiographyDynamicProfileJson
  ): string {
    const jobTitleOne: string = biosProfileData?.jobTitle1;
    const jobTitleTwo: string = biosProfileData?.jobTitle2;

    return (
      (jobTitleOne ?? '') +
      (jobTitleTwo && jobTitleOne ? ', ' : '') +
      (jobTitleTwo ?? '')
    );
  }

  private checkProfileImageUrl(
    biosProfileData: BiographyDynamicProfileJson
  ): string {
    if (
      biosProfileData?.imageUrl !== null ||
      biosProfileData?.imageUrl !== ''
    ) {
      return biosProfileData?.imageUrl;
    } else {
      return '/assets/images/blank-spacer.png';
    }
  }

  private createProfilePageUrl(
    biosProfileData: BiographyDynamicProfileJson,
    biographyPageUrl
  ): string {
    const docPath = biosProfileData?.documentPath;
    let extractProfilepath = '';
    if (docPath.indexOf(this.baseProfileDocumentPath) !== -1) {
      extractProfilepath = docPath.slice(this.baseProfileDocumentPath.length);
    } else {
      extractProfilepath = biosProfileData?.documentName;
    }
    if (biographyPageUrl !== null || biographyPageUrl !== '') {
      return `/${biographyPageUrl}/${extractProfilepath}`;
    } else {
      return '';
    }
  }
}
